import { InputMask } from 'primereact/inputmask';
import { useController, type FieldPathByValue, type FieldValues } from 'react-hook-form';
import { formatCoordinateInput } from '@/shared/lib/coordinateInput';
import classes from '@/shared/ui/RHFFields/RHFFields.module.scss';

interface CoordinateFieldProps<TValues extends FieldValues> {
    name: FieldPathByValue<TValues, string>;
    label: string;
    integerDigits: 2 | 3;
    disabled?: boolean;
}

/** Вводит координату с автоматической десятичной точкой */
export const CoordinateField = <TValues extends FieldValues>(
    {
        name,
        label,
        integerDigits,
        disabled,
    }: CoordinateFieldProps<TValues>
) => {
    const { field, fieldState } = useController<TValues>({ name });

    const value = typeof field.value === 'string'
        ? field.value
        : '';

    const negative = value.startsWith('-');
    const errorId = `${name}-error`;

    return <div className={classes.field}>
        <label
            htmlFor={name}
            className={classes.requiredLabel}
        >
            {label}
        </label>
        <InputMask
            id={name}
            name={field.name}
            ref={field.ref}
            value={value}
            mask={`${negative
                ? '-'
                : ''}${'9'.repeat(integerDigits)}.9999999`}
            placeholder={integerDigits === 2
                ? '55.0000000'
                : '037.0000000'
            }
            autoClear={false}
            disabled={disabled}
            onChange={(event) => field.onChange(event.value ?? '')}
            onBlur={() => {
                const entered = value.replace(/_/g, '').replace(/\.$/, '');

                if (entered && Number.isFinite(Number(entered))) {
                    field.onChange(formatCoordinateInput(Number(entered), integerDigits));
                }

                field.onBlur();
            }}
            onKeyDownCapture={(event) => {
                if (event.key !== '-' && event.key !== '+') return;

                event.preventDefault();
                event.stopPropagation();

                field.onChange(`${event.key === '-'
                    ? '-'
                    : ''}${value.replace(/^-/, '')}`);
            }}
            onPasteCapture={(event) => {
                const pasted = event.clipboardData.getData('text').trim().replace(',', '.');

                if (!/^[+-]?\d+\.\d+$/.test(pasted)) return;

                event.preventDefault();
                event.stopPropagation();
                field.onChange(formatCoordinateInput(Number(pasted), integerDigits));
            }}
            className={classes.control}
            invalid={fieldState.invalid}
            aria-required
            aria-invalid={fieldState.invalid}
            aria-describedby={fieldState.error
                ? errorId
                : undefined
            }
        />
        {fieldState.error &&
            <small
                id={errorId}
                className={classes.errorMessage}
                role="alert"
            >
                {fieldState.error.message}
            </small>
        }
    </div>;
};
